import React, { useRef } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined } from "@ant-design/icons";
import "./SortableList.scss";

export interface SortableRenderHelpers {
  /** Drag handle node — render it inside the row so dragging starts only here. */
  dragHandle: React.ReactNode;
  /** True while this row is the one being dragged (for visual feedback). */
  isDragging: boolean;
}

interface SortableListProps<T> {
  items: T[];
  /** Called with the reordered array after a successful drop. */
  onReorder: (items: T[]) => void;
  /** Renders a row; place `helpers.dragHandle` wherever the grip should live. */
  renderItem: (
    item: T,
    index: number,
    helpers: SortableRenderHelpers,
  ) => React.ReactNode;
  /**
   * Optional stable id per item. Prefer supplying this whenever a row contains
   * text inputs whose edits replace the row object (a fresh object identity
   * would otherwise remount the row and steal focus). When omitted, a synthetic
   * id is derived from object identity — safe for rows that only hold selects.
   */
  getId?: (item: T, index: number) => UniqueIdentifier;
}

interface SortableRowProps<T> {
  id: UniqueIdentifier;
  item: T;
  index: number;
  renderItem: SortableListProps<T>["renderItem"];
}

function SortableRow<T>({ id, item, index, renderItem }: SortableRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    boxShadow: isDragging ? "0 2px 12px rgba(0, 0, 0, 0.15)" : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  const dragHandle = (
    <button
      type="button"
      className="sortableDragHandle"
      aria-label="Reordenar (arrastra o usa las flechas del teclado)"
      {...attributes}
      {...listeners}
    >
      <HolderOutlined />
    </button>
  );

  return (
    <div ref={setNodeRef} className="sortableItem" style={style}>
      {renderItem(item, index, { dragHandle, isDragging })}
    </div>
  );
}

/**
 * Reusable drag-and-drop reordering wrapper built on @dnd-kit. Dragging starts
 * only from the explicit handle exposed to `renderItem`, so the existing
 * inputs, selects and buttons inside each row keep working. Keyboard reordering
 * is enabled via the handle (KeyboardSensor). On drop, the array is reordered
 * with `arrayMove` and handed back through `onReorder` — persistence stays with
 * the form's normal submit.
 */
function SortableList<T>({
  items,
  onReorder,
  renderItem,
  getId,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Identity-based fallback ids: stable across renders and reorders (arrayMove
  // preserves element references) and unique per render (each array slot is a
  // distinct object), without forcing an id field onto the caller's state.
  const idMapRef = useRef(new WeakMap<object, string>());
  const counterRef = useRef(0);

  const resolveId = (item: T, index: number): UniqueIdentifier => {
    if (getId) return getId(item, index);
    if (item && typeof item === "object") {
      const map = idMapRef.current;
      let id = map.get(item as object);
      if (!id) {
        counterRef.current += 1;
        id = `sortable-${counterRef.current}`;
        map.set(item as object, id);
      }
      return id;
    }
    return index;
  };

  const ids = items.map(resolveId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableRow
            key={ids[index]}
            id={ids[index]}
            item={item}
            index={index}
            renderItem={renderItem}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

export default SortableList;
